import React, { useContext, useState } from 'react'
import { useQuery } from '@apollo/client'
import styles from './TaskBoardView.module.scss'
import DeviceContext from '../context/DeviceContext.js'
import { GET_TASKS_QUERY } from '../query/gqlQueries.js'
import CVLogo from '../assets/CVLogo.svg'
import ArrowRightStop from '../assets/ArrowRightStop.svg'
import EditTaskDialog from './EditTaskDialog.js'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import VisibilityIcon from '@mui/icons-material/Visibility'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import SortIcon from '@mui/icons-material/Sort'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import FlagIcon from '@mui/icons-material/Flag'
import RefreshIcon from '@mui/icons-material/Refresh'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import BrushIcon from '@mui/icons-material/Brush'
import ViewInArIcon from '@mui/icons-material/ViewInAr'
import CodeIcon from '@mui/icons-material/Code'
import AssignmentIcon from '@mui/icons-material/Assignment'
import SchoolIcon from '@mui/icons-material/School'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import VideocamIcon from '@mui/icons-material/Videocam'

import DavidImage from '../images/David.jpg'
import SelinayImage from '../images/Selinay.jpg'
import TomasImage from '../images/Tomas.jpg'

const assigneeImages = {
  David: DavidImage,
  Selinay: SelinayImage,
  Tomas: TomasImage
}

export default function TaskBoardView({ isTaskBoardViewVisible, setIsTaskBoardViewVisible }) {
  const { isMobile } = useContext(DeviceContext)
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false)
  const [selectedTask, setSelectedTask] = useState(null)
  const [isDarkMode, setIsDarkMode] = useState(true)

  // Filter & Sort States
  const [filterProject, setFilterProject] = useState('')
  const [filterAssignee, setFilterAssignee] = useState('')
  const [filterPriority, setFilterPriority] = useState('')
  const [filterDueDate, setFilterDueDate] = useState('')
  const [filterStatuses, setFilterStatuses] = useState(['INPROGRESS', 'VALIDATING'])
  const [sortBy, setSortBy] = useState('') // 'priority' | 'dueDate'
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false)

  const username = 'David'
  // Fetch tasks using the query
  const { loading, error, data, refetch } = useQuery(GET_TASKS_QUERY, {
    variables: { username },
    skip: !username // Skip the query if username is not provided
  })

  // Handle loading and error states
  if (loading) return <div>Loading tasks...</div>
  if (error) return <div>Error fetching tasks: {error.message}</div>

  // Extract tasks from the query result
  let rawTasks = data?.getTasks || []

  // Extract unique projects and assignees for dropdowns
  const availableProjects = [...new Set(rawTasks.map((t) => t.properties.project).filter(Boolean))]
  const availableAssignees = [...new Set(rawTasks.map((t) => t.properties.assignee).filter(Boolean))]

  // Priority ranking
  const priorityRank = {
    urgent: 4,
    high: 3,
    medium: 2,
    med: 2,
    low: 1
  }

  if (filterProject) {
    rawTasks = rawTasks.filter((item) => item.properties.project === filterProject)
  }
  if (filterAssignee) {
    rawTasks = rawTasks.filter((item) => item.properties.assignee === filterAssignee)
  }
  if (filterPriority) {
    rawTasks = rawTasks.filter((item) => item.properties.priority?.toLowerCase() === filterPriority.toLowerCase())
  }
  if (filterDueDate) {
    rawTasks = rawTasks.filter((item) => {
      if (!item.properties.dueDate) return false
      const dateStr = new Date(item.properties.dueDate).toISOString().split('T')[0]
      return dateStr === filterDueDate
    })
  }
  if (filterStatuses.length > 0) {
    rawTasks = rawTasks.filter((item) => {
      const s = item.status ? item.status.toUpperCase() : 'UNASSIGNED'
      return filterStatuses.includes(s)
    })
  }

  if (sortBy === 'priority') {
    rawTasks = [...rawTasks].sort((a, b) => {
      const pA = priorityRank[a.properties.priority?.toLowerCase()] || 1
      const pB = priorityRank[b.properties.priority?.toLowerCase()] || 1
      return pB - pA
    })
  } else if (sortBy === 'dueDate') {
    rawTasks = [...rawTasks].sort((a, b) => {
      const dA = new Date(a.properties.dueDate || '9999-12-31').getTime()
      const dB = new Date(b.properties.dueDate || '9999-12-31').getTime()
      return dA - dB
    })
  }

  const tasks = rawTasks

  // Map priority to CSS class for styling
  const priorityToClass = {
    Urgent: styles.darkRed,
    High: styles.red,
    Medium: styles.orange,
    Med: styles.orange,
    Low: styles.green
  }

  const handleClose = () => {
    setIsTaskBoardViewVisible(false)
  }

  const toggleStatus = (status) => {
    setFilterStatuses((prev) => (prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]))
  }

  return (
    <div className={`${styles.TaskBoardFullView} ${isDarkMode ? styles.dark : styles.light}`}>
      <div className={`${styles.navBar} ${styles.grid}`}>
        <div className={styles.logo}>
          <img src={CVLogo} alt="Logo" />
        </div>
        <div className={styles.title}>Task Sheet</div>
        <div style={{ display: 'flex' }}>
          <div className={styles.themeToggle} onClick={() => setIsDarkMode(!isDarkMode)}>
            {isDarkMode ? '☀️' : '🌙'}
          </div>
          <div className={styles.closeButton} onClick={handleClose}>
            X
          </div>
        </div>
      </div>

      <div className={styles.filterBarContainer}>
        <div className={styles.filterBar}>
          {/* Project */}
          <select className={styles.filterSelect} value={filterProject} onChange={(e) => setFilterProject(e.target.value)}>
            <option value="">All Projects</option>
            {availableProjects.map((proj) => (
              <option key={proj} value={proj}>
                {proj}
              </option>
            ))}
          </select>

          {/* Assignees Avatars */}
          <div className={styles.filterGroup}>
            {availableAssignees.map((assignee) => (
              <div
                key={assignee}
                className={`${styles.avatarWrapper} ${filterAssignee === assignee ? styles.selected : ''}`}
                onClick={() => setFilterAssignee(filterAssignee === assignee ? '' : assignee)}
                title={assignee}>
                {assigneeImages[assignee] ? (
                  <img src={assigneeImages[assignee]} alt={assignee} />
                ) : (
                  <div className={styles.avatarFallback}>{assignee.charAt(0)}</div>
                )}
              </div>
            ))}
          </div>

          {/* Priority Circles */}
          <div className={styles.filterGroup}>
            {[
              { value: 'low', color: styles.green },
              { value: 'medium', color: styles.orange },
              { value: 'high', color: styles.red },
              { value: 'urgent', color: styles.darkRed }
            ].map((p) => (
              <div
                key={p.value}
                className={`${styles.circle} ${p.color} ${filterPriority === p.value ? styles.selectedCircle : ''}`}
                onClick={() => setFilterPriority(filterPriority === p.value ? '' : p.value)}
                title={p.value}></div>
            ))}
          </div>

          <input
            type="date"
            className={styles.filterInput}
            value={filterDueDate}
            onChange={(e) => setFilterDueDate(e.target.value)}
            title="Filter by Due Date"
          />

          {/* Sort Menu */}
          <div className={styles.sortMenuContainer}>
            <button className={`${styles.iconBtn} ${sortBy ? styles.activeSort : ''}`} onClick={() => setIsSortMenuOpen(!isSortMenuOpen)} title="Sort Tasks">
              <SortIcon />
            </button>
            {isSortMenuOpen && (
              <div className={styles.dropdownMenuSort}>
                <div
                  className={`${styles.dropdownItem} ${sortBy === 'priority' ? styles.active : ''}`}
                  onClick={() => {
                    setSortBy(sortBy === 'priority' ? '' : 'priority')
                    setIsSortMenuOpen(false)
                  }}>
                  <FlagIcon fontSize="small" /> Sort by Priority
                </div>
                <div
                  className={`${styles.dropdownItem} ${sortBy === 'dueDate' ? styles.active : ''}`}
                  onClick={() => {
                    setSortBy(sortBy === 'dueDate' ? '' : 'dueDate')
                    setIsSortMenuOpen(false)
                  }}>
                  <CalendarMonthIcon fontSize="small" /> Sort by Due Date
                </div>
              </div>
            )}
          </div>
          <button className={styles.iconBtn} onClick={() => refetch()} title="Refresh Tasks">
            <RefreshIcon fontSize="small" />
          </button>
          <button
            className={styles.clearBtn}
            onClick={() => {
              setFilterProject('')
              setFilterAssignee('')
              setFilterPriority('')
              setFilterDueDate('')
              setFilterStatuses([])
              setSortBy('')
            }}>
            Clear Filters
          </button>
        </div>
      </div>

      <div className={styles.filterBarContainer}>
        <div className={styles.statusToggleGroup}>
          <button
            className={`${styles.statusToggleBtn} ${filterStatuses.includes('UNASSIGNED') ? styles.active : ''}`}
            onClick={() => toggleStatus('UNASSIGNED')}
            title="Unassigned Status">
            <HelpOutlineIcon fontSize="small" />
          </button>
          <button
            className={`${styles.statusToggleBtn} ${filterStatuses.includes('BACKLOG') ? styles.active : ''}`}
            onClick={() => toggleStatus('BACKLOG')}
            title="Backlog">
            <FormatListBulletedIcon fontSize="small" />
          </button>
          <button
            className={`${styles.statusToggleBtn} ${filterStatuses.includes('INPROGRESS') ? styles.active : ''}`}
            onClick={() => toggleStatus('INPROGRESS')}
            title="Doing">
            <AutorenewIcon fontSize="small" />
          </button>
          <button
            className={`${styles.statusToggleBtn} ${filterStatuses.includes('VALIDATING') ? styles.active : ''}`}
            onClick={() => toggleStatus('VALIDATING')}
            title="Validating">
            <VisibilityIcon fontSize="small" />
          </button>
          <button
            className={`${styles.statusToggleBtn} ${filterStatuses.includes('DONE') ? styles.active : ''}`}
            onClick={() => toggleStatus('DONE')}
            title="Completed">
            <CheckCircleIcon fontSize="small" />
          </button>
        </div>
      </div>

      <div className={`${styles.taskContainer} ${styles.grid4}`}>
        <div
          className={styles.taskCardSpacer}
          onClick={() => {
            setSelectedTask(null)
            setIsTaskDialogOpen(true)
          }}>
          <div className={styles.addSign}>+</div>
          <div className={styles.availableTasks}>Add Task</div>
        </div>
        {tasks.map((task) => {
          const taskClass = priorityToClass[task.properties.priority] || styles.grey

          // Hour dot visualizer
          const MAX_DOTS = 8
          const timeBudget = parseInt(task.properties.timeBudget) || 0
          const filledDots = Math.min(timeBudget, MAX_DOTS)

          // Type icon map (matches EditTaskDialog types)
          const typeIconMap = [
            { label: 'Design',   icon: <BrushIcon fontSize="small" /> },
            { label: '3D',       icon: <ViewInArIcon fontSize="small" /> },
            { label: 'Coding',   icon: <CodeIcon fontSize="small" /> },
            { label: 'Admin',    icon: <AssignmentIcon fontSize="small" /> },
            { label: 'Learning', icon: <SchoolIcon fontSize="small" /> },
            { label: 'Photography', icon: <PhotoCameraIcon fontSize="small" /> },
            { label: 'Video',    icon: <VideocamIcon fontSize="small" /> },
          ]
          const typeEntry = typeIconMap.find(
            (t) => t.label.toLowerCase() === (task.properties.type || '').toLowerCase()
          )
          const typeIcon = typeEntry?.icon ?? <AssignmentIcon fontSize="small" />
          const typeLabel = typeEntry?.label ?? task.properties.type ?? 'Task'

          return (
            <div
              key={task.identity}
              className={`${styles.taskCard} ${taskClass}`}
              onClick={() => {
                setSelectedTask(task)
                setIsTaskDialogOpen(true)
              }}>

              {/* Header: title + type badge */}
              <div className={styles.cardHeader}>
                <h3 className={styles.taskName}>{task.properties.title}</h3>
                <div className={styles.typeIcon} title={typeLabel}>{typeIcon}</div>
              </div>

              {/* Stats: hour badge + dot visualizer */}
              <div className={styles.statsRow}>
                <div className={styles.hourBadge}>{task.properties.timeBudget} hrs</div>
                <div className={styles.hourVisualizer}>
                  {Array.from({ length: MAX_DOTS }, (_, i) => (
                    <span key={i} className={i < filledDots ? styles.filled : ''} />
                  ))}
                </div>
              </div>

              {/* Footer: project name */}
              <div className={styles.taskFooter}>
                <span className={styles.projectName}>
                  {task.properties.project || 'No project'}
                </span>
              </div>
            </div>
          )
        })}
      </div>
      <EditTaskDialog
        isOpen={isTaskDialogOpen}
        onClose={() => {
          setIsTaskDialogOpen(false)
          setSelectedTask(null)
        }}
        task={selectedTask}
      />
    </div>
  )
}
